import { useCallback } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';
import type { RecalculationProcessParams } from '@/api/domains/recalculation';


export interface UseRecalculoProcessResult {
  handleRecalculate: (params: RecalculationProcessParams) => void;
  handleStop: () => void;
}

export function useRecalculoProcess(): UseRecalculoProcessResult {
  const { t: translate } = useTranslation();
  const { openModal, closeModal, showSuccess, showApiError } = useUI();
  const { createSignal } = useAbortController();

  const handleRecalculate = useCallback((params: RecalculationProcessParams) => {
    openModal('ConfirmModal', {
      variant: 'success',
      title: translate('attendance:recalculo.modals.start.title'),
      bodyText: translate('attendance:recalculo.modals.start.body'),
      confirmText: translate('attendance:recalculo.modals.start.confirm'),
      cancelText: translate('common:actions.cancel'),
      onConfirm: async () => {
        const signal = createSignal();
        const response = await recalculationMonthlyCutsService.startProcess(params, signal);

        if (response.error?.code === 'ABORTED') return;

        if (!response.success) {
          showApiError(response, translate('attendance:recalculo.errors.startProcess'));
          return;
        }

        showSuccess(translate('attendance:recalculo.messages.startSuccess'));
        closeModal('ConfirmModal');
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [openModal, closeModal, showSuccess, showApiError, translate, createSignal]);

  const handleStop = useCallback(() => {
    openModal('ConfirmModal', {
      variant: 'error',
      title: translate('attendance:recalculo.modals.stop.title'),
      bodyText: translate('attendance:recalculo.modals.stop.body'),
      confirmText: translate('attendance:recalculo.modals.stop.confirm'),
      cancelText: translate('common:actions.cancel'),
      onConfirm: async () => {
        const signal = createSignal();
        const response = await recalculationMonthlyCutsService.stopProcess(signal);

        if (response.error?.code === 'ABORTED') return;

        if (!response.success) {
          showApiError(response, translate('attendance:recalculo.errors.stopProcess'));
          return;
        }

        showSuccess(translate('attendance:recalculo.messages.stopSuccess'));
        closeModal('ConfirmModal');
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [openModal, closeModal, showSuccess, showApiError, translate, createSignal]);

  return { handleRecalculate, handleStop };
}

export default useRecalculoProcess;
